import { useEffect, useMemo, useRef } from 'react';
import type { PublicItem } from '@im-marina/shared';
import { columnsFor, layoutMasonry } from '../../lib/masonry';
import { aspectRatio } from '../../lib/media';
import { useElementWidth } from '../../hooks/useElementWidth';
import { useNoHover } from '../../hooks/useMediaQuery';
import { ItemCard, CAPTION_HEIGHT } from '../ItemCard/ItemCard';
import styles from './MasonryGrid.module.css';

type Props = { items: PublicItem[]; gap: number };

/**
 * Fixed-width, variable-height columns. Items are absolutely positioned so DOM (and tab)
 * order stays in portfolio order regardless of which column each lands in.
 */
export function MasonryGrid({ items, gap }: Props) {
  const ref = useRef<HTMLUListElement>(null);
  const width = useElementWidth(ref);
  const noHover = useNoHover();
  const columns = columnsFor(width);

  const layout = useMemo(
    () =>
      layoutMasonry({
        aspects: items.map((i) => aspectRatio(i.cover)),
        containerWidth: width,
        columns,
        gap,
        extraHeight: noHover ? CAPTION_HEIGHT : 0,
      }),
    [items, width, columns, gap, noHover],
  );

  // Fade cards in as they enter the viewport.
  useEffect(() => {
    const list = ref.current;
    if (!list) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.setAttribute('data-visible', '');
            io.unobserve(e.target);
          }
        }),
      { rootMargin: '0px 0px -10% 0px' },
    );
    list.querySelectorAll('li:not([data-visible])').forEach((li) => io.observe(li));
    return () => io.disconnect();
  }, [items, width]);

  return (
    <ul ref={ref} className={styles.grid} style={{ height: layout.height }}>
      {width > 0 &&
        items.map((item, i) => {
          const box = layout.boxes[i]!;
          return (
            <li
              key={item.id}
              className={styles.cell}
              style={{
                transform: `translate(${box.x}px, ${box.y}px)`,
                width: box.width,
                height: box.height,
              }}
            >
              <ItemCard item={item} columns={columns} showCaption={noHover} />
            </li>
          );
        })}
    </ul>
  );
}
